// Entretien: échéances des plans et des papiers, immobilisations, indicateurs de
// l'aperçu. Ports de backend/app/services/maintenance_service.py
// (_compute_plan_due, evaluate_plan, evaluate_document, overview).

const num = (valeur) => {
  const n = Number.parseFloat(valeur);
  return Number.isFinite(n) ? n : 0;
};

const jourLocal = (maintenant = new Date()) =>
  new Date(Date.UTC(maintenant.getFullYear(), maintenant.getMonth(), maintenant.getDate()));

const lireDate = (valeur) => {
  if (!valeur) return null;
  const texte = String(valeur).trim().slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(texte);
  if (!m) return null;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
};

const ajouterJours = (base, jours) => {
  const d = new Date(base.getTime() + jours * 86400000);
  return d.toISOString().slice(0, 10);
};

const joursEntre = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

export function calculerEcheanceDuPlan(plan, aujourdhui = jourLocal()) {
  const maj = {};
  const declencheur = plan.trigger_type || 'mileage';
  if (['mileage', 'both'].includes(declencheur) && num(plan.interval_km)) {
    maj.next_due_km = num(plan.last_service_km) + num(plan.interval_km);
  }
  if (['date', 'both'].includes(declencheur) && Number.parseInt(plan.interval_days || 0, 10)) {
    const base = lireDate(plan.last_service_date) || aujourdhui;
    maj.next_due_date = ajouterJours(base, Number.parseInt(plan.interval_days, 10));
  }
  return maj;
}

export function evaluerPlan(plan, valeurCourante, aujourdhui = jourLocal()) {
  let etat = 'ok';
  let kmRestant = null;
  let joursRestants = null;
  const raisons = [];
  const declencheur = plan.trigger_type || 'mileage';

  if (['mileage', 'both'].includes(declencheur) && plan.next_due_km !== null && plan.next_due_km !== undefined) {
    if (valeurCourante !== null && valeurCourante !== undefined) {
      kmRestant = num(plan.next_due_km) - num(valeurCourante);
      const seuil = num(plan.alert_threshold_km) || num(plan.interval_km) * 0.1;
      if (kmRestant <= 0) {
        etat = 'overdue';
        raisons.push(`Dépassé de ${Math.abs(kmRestant).toFixed(0)} km`);
      } else if (kmRestant <= seuil) {
        etat = 'due_soon';
        raisons.push(`Encore ${kmRestant.toFixed(0)} km`);
      }
    } else {
      raisons.push('Aucun relevé compteur');
    }
  }
  if (['date', 'both'].includes(declencheur) && plan.next_due_date) {
    const echeance = lireDate(plan.next_due_date);
    if (echeance) {
      joursRestants = joursEntre(echeance, aujourdhui);
      const seuilJours = Number.parseInt(plan.alert_threshold_days || 15, 10);
      if (joursRestants <= 0) {
        etat = 'overdue';
        raisons.push(`Échue depuis ${Math.abs(joursRestants)} j`);
      } else if (joursRestants <= seuilJours && etat !== 'overdue') {
        etat = 'due_soon';
        raisons.push(`Dans ${joursRestants} j`);
      }
    }
  }
  return {
    ...plan,
    due_state: etat,
    km_remaining: kmRestant,
    days_remaining: joursRestants,
    current_value: valeurCourante ?? null,
    due_reason: raisons.join(' · ') || 'À jour',
  };
}

export function evaluerPapier(papier, aujourdhui = jourLocal()) {
  const expiration = lireDate(papier.expiry_date);
  if (!expiration) return { ...papier, expiry_state: 'unknown', days_remaining: null };
  const jours = joursEntre(expiration, aujourdhui);
  const seuil = Number.parseInt(papier.alert_days_before || 30, 10);
  let etat;
  if (jours < 0) etat = 'expired';
  else if (jours <= seuil) etat = 'expiring';
  else etat = 'valid';
  return { ...papier, expiry_state: etat, days_remaining: jours };
}

/** Dernier relevé par camion (type « km » d'abord, comme le serveur les range par date). */
export function dernierReleveParCamion(releves) {
  const parCamion = {};
  [...releves]
    .sort((a, b) => String(a.reading_date || a.created_at || '').localeCompare(String(b.reading_date || b.created_at || '')))
    .forEach((r) => {
      if (r.truck_id) parCamion[r.truck_id] = r;
    });
  return parCamion;
}

export function calculerApercuEntretien({ plans, papiers, ordres, pannes, immobilisations, releves, aujourdhui = jourLocal() }) {
  const derniers = dernierReleveParCamion(releves);
  const plansEvalues = plans.map((p) => evaluerPlan(p, derniers[p.truck_id] ? num(derniers[p.truck_id].value) : null, aujourdhui));
  const papiersEvalues = papiers.filter((p) => p.is_active !== false).map((p) => evaluerPapier(p, aujourdhui));
  const ordresOuverts = ordres.filter((o) => ['draft', 'planned', 'in_progress'].includes(o.status));
  const ordresFaits = ordres.filter((o) => o.status === 'done');
  const pannesOuvertes = pannes.filter((b) => ['open', 'acknowledged'].includes(b.status));
  const actives = immobilisations.filter((i) => i.status === 'active');
  return {
    plans_overdue: plansEvalues.filter((p) => p.due_state === 'overdue').length,
    plans_due_soon: plansEvalues.filter((p) => p.due_state === 'due_soon').length,
    plans_total: plansEvalues.length,
    documents_expired: papiersEvalues.filter((d) => d.expiry_state === 'expired').length,
    documents_expiring: papiersEvalues.filter((d) => d.expiry_state === 'expiring').length,
    documents_total: papiersEvalues.length,
    work_orders_open: ordresOuverts.length,
    work_orders_in_progress: ordres.filter((o) => o.status === 'in_progress').length,
    work_orders_total: ordres.length,
    breakdowns_open: pannesOuvertes.length,
    breakdowns_critical_open: pannesOuvertes.filter((b) => b.severity === 'critical').length,
    immobilized_trucks: new Set(actives.map((i) => i.truck_id)).size,
    maintenance_spend: ordresFaits.reduce((s, o) => s + num(o.total_cost), 0),
    maintenance_spend_committed: ordres.reduce((s, o) => s + num(o.total_cost), 0),
  };
}
